import { useCallback, useEffect, useState } from 'react';
import { CalendarCheck2, CalendarClock } from 'lucide-react';
import { api, ErrorApi } from '../api';
import EstadoVacio from '../ui/EstadoVacio';
import NotaExplicativa from '../ui/NotaExplicativa';
import ChipEstado from '../ui/ChipEstado';
import { VIGENCIA_INSTRUMENTO } from '../ui/estados';
import { cuantoFalta, fechaLarga } from '../ui/calidad';
import { Cargando } from '../ui/EstadoDePantalla';

// QUÉ HAY QUE MANDAR A CALIBRAR · Fase 5 del módulo de Calidad.
//
// PANTALLA APARTE Y NO UN FILTRO DE «Instrumentos», y es la decisión que
// decide si esto se usa. Son dos preguntas distintas, las hace la misma
// persona en dos momentos distintos, y una sola pantalla obligaría a
// quien llega el lunes con la pregunta urgente —«¿qué se me está
// pasando?»— a filtrar un catálogo para encontrarla.
//
// TRES DECISIONES DE USUARIO CERO:
//
//   1. **Lo vencido va primero y en rojo.** Con esos no se puede medir
//      HOY: no es un aviso, es una parada.
//   2. **Se dice cuántos días faltan o cuántos van de retraso**, no una
//      fecha suelta. «Vence el 12 de octubre» obliga a hacer la resta;
//      «se pasó hace 9 días» ya está hecha.
//   3. **Vacío no es una pantalla en blanco**: es la buena noticia, y se
//      dice como tal.

export default function Vencimientos() {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);

  const cargar = useCallback(() => {
    api.get('/instrumentos/vencimientos')
      .then(setDatos)
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar.'));
  }, []);

  useEffect(cargar, [cargar]);

  const lista = datos?.instrumentos || [];
  const parados = lista.filter((i) => !i.vigente);
  const proximos = lista.filter((i) => i.vigente);

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-bold text-gray-900">Qué hay que calibrar</h1>
        <p className="text-sm text-gray-600">
          Los instrumentos que se pasaron de su calibración y los que están a
          punto. Con los primeros no se puede medir hasta que vuelvan del
          laboratorio.
        </p>
      </header>

      {error && <div role="alert" className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      {datos === null && <Cargando filas={4} />}

      {datos !== null && lista.length === 0 && (
        <EstadoVacio
          icono={CalendarCheck2}
          titulo="Nada que mandar al laboratorio"
          descripcion={
            `Todos los instrumentos en uso tienen su calibración vigente y a `
            + `ninguno le faltan menos de ${datos.dias_aviso} días. Vuelve por `
            + `aquí de vez en cuando: TEGO también te avisa solo cuando alguno `
            + `se acerque.`
          }
        />
      )}

      {parados.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-semibold text-gray-900">
            No se puede medir con estos ({parados.length})
          </h2>
          {parados.map((i) => (
            <div key={i.id}
                 className="border border-red-200 bg-red-50 rounded-xl p-3 flex items-start gap-3">
              <CalendarClock className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900">
                  {i.codigo} · {i.descripcion}
                </div>
                <div className="text-sm text-gray-800">{cuantoFalta(i)}</div>
                {i.proxima_fecha && (
                  <div className="text-sm text-gray-600">
                    Su calibración valía hasta el {fechaLarga(i.proxima_fecha)}.
                  </div>
                )}
                {i.ubicacion && (
                  <div className="text-sm text-gray-600">Está en {i.ubicacion}.</div>
                )}
              </div>
              <ChipEstado mapa={VIGENCIA_INSTRUMENTO} clave={i.vigencia} />
            </div>
          ))}
        </section>
      )}

      {proximos.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-semibold text-gray-900">
            Se van a pasar pronto ({proximos.length})
          </h2>
          {proximos.map((i) => (
            <div key={i.id}
                 className="border border-amber-200 bg-amber-50 rounded-xl p-3 flex items-start gap-3">
              <CalendarClock className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900">
                  {i.codigo} · {i.descripcion}
                </div>
                <div className="text-sm text-gray-800">{cuantoFalta(i)}</div>
                <div className="text-sm text-gray-600">
                  Vence el {fechaLarga(i.proxima_fecha)}.
                </div>
              </div>
              <ChipEstado mapa={VIGENCIA_INSTRUMENTO} clave={i.vigencia} />
            </div>
          ))}
        </section>
      )}

      {datos !== null && lista.length > 0 && (
        <NotaExplicativa titulo="Mandarlo al laboratorio se apunta aquí también">
          Mientras esté fuera, cámbialo a «fuera de servicio» en su ficha: así
          nadie intenta medir con un instrumento que no está en la planta.
          Cuando vuelva, registra su calibración nueva y se vuelve a habilitar
          solo.
        </NotaExplicativa>
      )}
    </div>
  );
}
